import React, {useCallback, useEffect, useState} from 'react';
import {connect, useSelector} from 'react-redux';
import {compose} from 'redux';
import {withRouter} from 'react-router-dom';
import findIndex from 'lodash/findIndex';
import PropTypes from 'prop-types';
import {Box, Slide} from '@spothero/ui';
import StorageUtils from '@spothero/utils/storage';
import withConfig from '@/config/withConfig';
import {push} from 'store/router/router-actions';
import {routeToSpotDetails} from 'router/router-utils';
import {loadEventPackageSpot} from 'store/spots/spots-event-packages-actions';
import {getSpotsV2} from 'store/spots/spots-selectors';
import {setSelectedSpotId} from 'store/spots/spots-actions';
import {
    PROPTYPE_EVENT_PACKAGE_FACILITY_SUMMARY,
    PROPTYPE_EVENT_PACKAGE,
    PROPTYPE_EVENT_PACKAGE_VENUE,
} from '@/proptypes/index';

import QuickFilters from 'common/search-filters-controls';
import SpotList from 'common/spot-list';
import SearchMap from 'common/search-results-map/search-map';
import DetailsModal from 'common/event-package-info/details-modal';

import SpotListItemEventPackage from './components/spot-card';
import EventPackageDetails from './components/event-package-details';
import EventPackageDetailsCondensed from './components/event-package-details-condensed';
import SpotDetails from './components/spot-details';
import ResponsiveSearchLayout from './layouts/responsive-search-layout';
import useStickyScroll from 'hooks/use-sticky-scroll';
import {setSpotDetailReferrer} from 'store/spot/spot-actions';
import SearchControlsInfo from 'common/search-controls/search-controls-info/SearchControlsInfo';
import useIsMobile from 'hooks/use-is-mobile';
import {SearchPageView} from 'utils/search-page-view';
import {GoogleMapsProvider} from 'common/google-map-context';
import PriceBreakdownModal from 'common/price-breakdown-modal';

import {useFetchStatesWithFeeIncluded} from 'pages/search/hooks/total-price-toggle/use-fetch-states-with-fee-included';
import {useFirebase} from 'hooks/use-firebase';
import {useSyncTotalPriceToggleOnSearch} from 'pages/search/hooks/total-price-toggle/use-sync-total-price-toggle-on-search';
import {useSyncTotalPriceToggleOnMount} from 'pages/search/hooks/total-price-toggle/use-sync-total-price-toggle-on-mount';
import SearchUtils from 'utils/search-utils';

const EventPackagesSearch = ({
    loadSpots,
    spots,
    selectedSpotId,
    eventPackage,
    eventPackageVenue,
    sortBy,
    updateSelectedSpotId,
    pushTo,
    setSpotDetailReferrerTo,
    siteUrl,
    query,
    priceBreakdownModalSpotId,
}) => {
    useFirebase();
    useFetchStatesWithFeeIncluded();
    useSyncTotalPriceToggleOnMount();
    useSyncTotalPriceToggleOnSearch();
    const isMobile = useIsMobile();
    const {view: searchPageView} = useSelector(state => state.search.data);
    const [isMobileMapView, setIsMobileMapView] = useState(false);

    useEffect(() => {
        setIsMobileMapView(searchPageView === SearchPageView.MOBILE_MAP);
    }, [searchPageView]);

    // Load search results, given an event package id
    useEffect(() => {
        loadSpots({eventPackageId: eventPackage.id});
    }, [eventPackage.id, loadSpots]);

    // Set up breadcrumbs to mimic what we get from redux on other search pages
    const eventPackageBreadcrumbs = {
        city: {
            url: `${siteUrl}/event-packages?kind=destination&id=${eventPackageVenue.id}`,
            displayName: `${eventPackageVenue.title} Event Packages`,
        },
        activeBreadcrumb: {
            label: eventPackage.title,
        },
    };

    // Add spot-id query param from URL which sets non-null Spot ID and toggles appearance of Spot Details modal
    useEffect(() => {
        const urlParams = new URLSearchParams(query);
        const querySpotId = urlParams.get('spot-id');

        updateSelectedSpotId({
            spotId: querySpotId,
        });
    }, [query, updateSelectedSpotId]);

    // Remove spot-id query param from URL which sets null Spot ID and toggles disappearance of Spot Details modal
    const onSpotDetailsHidden = useCallback(() => {
        setSpotDetailReferrerTo(null);
        routeToSpotDetails({
            method: pushTo,
            location,
            spotId: null,
            historyState: {reload: false},
        });
        StorageUtils.remove(SearchUtils.SEGMENT_SPOT_META);
    }, [pushTo, setSpotDetailReferrerTo]);

    const onShowDetailsClick = useCallback(
        spotId => {
            StorageUtils.set(SearchUtils.SEGMENT_SPOT_META, {
                rank: findIndex(spots, {spotId}) + 1,
                sortBy,
            });
        },
        [sortBy, spots]
    );

    // Show/hide modal listing events in this package
    const [
        eventPackageDetailsModalVisible,
        setEventPackageDetailsModalVisibility,
    ] = useState(false);
    const toggleEventPackagesDetailsModalVisibility = () =>
        setEventPackageDetailsModalVisibility(show => !show);

    // Show/hide sticky event package information above the spot list
    const [showStickyEventPackageDetails, onScroll] = useStickyScroll(
        '.EventPackageDetails'
    );

    return (
        <GoogleMapsProvider>
            <Box as="main" data-testid="EventPackagesSearch">
                <ResponsiveSearchLayout
                    zone1={
                        <>
                            <Box className="controls-container" boxShadow="md">
                                <SearchControlsInfo
                                    isMobile={isMobile}
                                    searchType="event-package"
                                    breadcrumbs={eventPackageBreadcrumbs}
                                />
                                <EventPackageDetails
                                    eventPackage={eventPackage}
                                    onClick={
                                        toggleEventPackagesDetailsModalVisibility
                                    }
                                />
                                <QuickFilters
                                    isMobile={isMobile}
                                    showReservationTypes={false}
                                    visibleSlugs={[]}
                                />
                            </Box>

                            <Slide
                                direction="top"
                                in={showStickyEventPackageDetails}
                                unmountOnExit
                                // zIndex="layer4" does not work as expected
                                // eslint-disable-next-line react/forbid-component-props
                                style={{zIndex: 400}}
                            >
                                <EventPackageDetailsCondensed
                                    eventPackage={eventPackage}
                                    onClick={
                                        toggleEventPackagesDetailsModalVisibility
                                    }
                                />
                            </Slide>
                            {!isMobileMapView ? (
                                <SpotList
                                    spots={spots}
                                    selectedSpotId={selectedSpotId}
                                    onShowDetailsClick={onShowDetailsClick}
                                    SpotListItem={SpotListItemEventPackage}
                                    showSpotListSorter
                                />
                            ) : null}
                        </>
                    }
                    zone1OnScroll={onScroll}
                    zone2={
                        <>
                            {isMobileMapView ? (
                                <SpotList
                                    spots={spots}
                                    selectedSpotId={selectedSpotId}
                                    onShowDetailsClick={onShowDetailsClick}
                                    SpotListItem={SpotListItemEventPackage}
                                />
                            ) : null}
                            <SearchMap
                                spots={spots}
                                onShowDetailsClick={onShowDetailsClick}
                            />
                            <Box display={{base: 'block', tablet: 'none'}}>
                                <QuickFilters className="QuickFilters-map" />
                                <Box
                                    position="fixed"
                                    top="0"
                                    left="0"
                                    right="0"
                                    zIndex="layer4"
                                >
                                    <EventPackageDetailsCondensed
                                        eventPackage={eventPackage}
                                        onClick={
                                            toggleEventPackagesDetailsModalVisibility
                                        }
                                    />
                                </Box>
                            </Box>
                        </>
                    }
                    zone3={
                        Boolean(selectedSpotId) && (
                            <SpotDetails
                                spotId={selectedSpotId}
                                eventPackage={eventPackage}
                                eventPackageVenue={eventPackageVenue}
                                onCloseClick={onSpotDetailsHidden}
                            />
                        )
                    }
                />
                <DetailsModal
                    modalVisible={eventPackageDetailsModalVisible}
                    activePackage={{
                        eventPackage,
                        destination: eventPackageVenue,
                    }}
                    onHidden={toggleEventPackagesDetailsModalVisibility}
                />
                {priceBreakdownModalSpotId && (
                    <PriceBreakdownModal spotId={priceBreakdownModalSpotId} />
                )}
            </Box>
        </GoogleMapsProvider>
    );
};

EventPackagesSearch.propTypes = {
    loadSpots: PropTypes.func.isRequired,
    spots: PropTypes.arrayOf(PROPTYPE_EVENT_PACKAGE_FACILITY_SUMMARY),
    selectedSpotId: PropTypes.string,
    eventPackage: PROPTYPE_EVENT_PACKAGE.isRequired,
    eventPackageVenue: PROPTYPE_EVENT_PACKAGE_VENUE.isRequired,
    sortBy: PropTypes.string,
    updateSelectedSpotId: PropTypes.func.isRequired,
    pushTo: PropTypes.func.isRequired,
    setSpotDetailReferrerTo: PropTypes.func,
    siteUrl: PropTypes.string.isRequired,
    query: PropTypes.string,
    priceBreakdownModalSpotId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
};

const mapStateToProps = (state, ownProps) => {
    const {
        eventPackage: {data: eventPackage},
        spots: {selectedSpotId},
        search: {
            data: {priceBreakdownModalSpotId},
        },
    } = state;
    const {
        history: {
            location: {search: query},
        },
    } = ownProps;

    return {
        eventPackageVenue: eventPackage.venue,
        eventPackage,
        spots: getSpotsV2(state),
        selectedSpotId,
        query,
        priceBreakdownModalSpotId,
    };
};

const mapDispatchToProps = {
    loadSpots: loadEventPackageSpot,
    updateSelectedSpotId: setSelectedSpotId,
    pushTo: push,
    setSpotDetailReferrerTo: setSpotDetailReferrer,
};

const enhance = compose(
    withConfig(['siteUrl']),
    withRouter,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(EventPackagesSearch);
